import { useCallback } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { ContentLoader, Modal, ModalControlModalProps, Text, TextField, useFormField } from '@frontend/design-system';
import { useAverageProductionAmount } from '../hooks/use-average-production-amount';
import { useHasAdminAccess } from '../hooks/use-has-admin-access';
import { generateTrackingId, TrackingIds } from '../tracking-ids';

interface EditAverageProductionModalProps extends ModalControlModalProps {
  baseTrackingId?: string;
  onSuccess?: () => void;
}

export const EditAverageProductionModal = ({
  baseTrackingId,
  onSuccess,
  ...modalProps
}: EditAverageProductionModalProps) => {
  const { t } = useTranslation('analytics');

  const {
    averageProductionAmount,
    isLoading: isLoadingAverageProductionAmount,
    updateAverageProductionAmount,
  } = useAverageProductionAmount();
  const hasAdminAccess = useHasAdminAccess();

  const fieldProps = useFormField(
    {
      type: 'text',
      value: `$${averageProductionAmount?.value?.toString() || '0'}`,
      required: true,
      transformValue: (value) => {
        if (!value) {
          return '$';
        }

        const numericValue = value.replace(/[^0-9]/g, '');
        return numericValue ? `$${numericValue}` : '$';
      },
    },
    [modalProps.show]
  );

  const handleSave = useCallback(async () => {
    const numericValue = fieldProps.value.replace(/[^0-9]/g, '');

    if (numericValue) {
      try {
        await updateAverageProductionAmount(parseInt(numericValue));
        onSuccess?.();
        modalProps.onClose();
      } catch (error) {
        console.error('Error updating average production amount:', error);
        sentry.warn({
          team: 'analytics',
          topic: 'analytics',
          error: `Failed to update average production amount: ${
            error instanceof Error ? error.message : String(error)
          }`,
        });
      }
    }
  }, [fieldProps.value, updateAverageProductionAmount, modalProps, onSuccess]);

  return (
    <Modal
      show={modalProps.show}
      onClose={modalProps.onClose}
      disableCloseOnEscape
      disableCloseOnOverlayClick
      maxWidth={420}
    >
      <Modal.Header
        onClose={modalProps.onClose}
        closeBtnTrackingId={generateTrackingId({
          trackingId: TrackingIds.editAverageProductionAmountModal.closeButton,
          base: baseTrackingId,
        })}
      >
        {t('Update Average Appointment Value')}
      </Modal.Header>
      <Modal.Body>
        <TextField
          {...fieldProps}
          data-testid='textfield'
          label={t('Average Appointment Value')}
          name='averageAppointmentValue'
        />
        <div css={styles.infoWrapper}>
          <Icon color='primary' name='info-filled' />
          <Text>{t('Updates here will be reflected in the overall production value for all locations')}</Text>
        </div>
        <ContentLoader show={isLoadingAverageProductionAmount} size='small' />
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Save'),
          disabled:
            fieldProps.value === '' || fieldProps.value === '$' || isLoadingAverageProductionAmount || !hasAdminAccess,
          onClick: handleSave,
          trackingId: generateTrackingId({
            trackingId: TrackingIds.editAverageProductionAmountModal.saveButton,
            base: baseTrackingId,
          }),
        }}
        secondary={{
          disabled: isLoadingAverageProductionAmount,
          label: t('Cancel'),
          onClick: modalProps.onClose,
          trackingId: generateTrackingId({
            trackingId: TrackingIds.editAverageProductionAmountModal.closeButton,
            base: baseTrackingId,
          }),
        }}
      />
    </Modal>
  );
};

const styles = {
  infoWrapper: css`
    align-items: center;
    background-color: ${theme.colors.primary5};
    border-radius: ${theme.borderRadius.medium};
    border: 1px solid ${theme.colors.primary50};
    display: flex;
    font-size: ${theme.font.size.medium};
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2)};

    > svg {
      height: ${theme.spacing(3)};
      min-height: ${theme.spacing(3)};
      min-width: ${theme.spacing(3)};
      width: ${theme.spacing(3)};
    }
  `,
};
